/**
 * Message Input Component
 * Input area for sending messages
 * Matches main app's input design (React Native)
 */

import { useState } from 'react';
import { IconButton, Platform, StyleSheet, TextInput, useTheme, View } from '@/components/common';
import { useTranslation } from '@/lib/i18n';
import type { MessageInputProps } from '../../types/chat';

export function MessageInput({
  onSend,
  disabled = false,
  placeholder,
  maxLength = 2000,
  showVoiceInput = false,
  autoFocus = false,
}: MessageInputProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  const resolvedPlaceholder = placeholder ?? t('chat.input.placeholder', 'Ask XEdu AI');
  const canSend = Boolean(message.trim()) && !disabled;

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e: any) => {
    // On web, users expect Enter to send. Keep Shift+Enter for a newline.
    if (disabled) return;
    if (Platform.OS !== 'web') return;

    const key = e?.nativeEvent?.key;
    if (key !== 'Enter') return;

    if (e?.nativeEvent?.shiftKey) return;

    e?.preventDefault?.();
    handleSend();
  };

  return (
    <View
      style={[
        styles.container,
        {
          borderTopColor: theme.colors.outlineVariant,
          backgroundColor: theme.colors.background,
        },
      ]}
    >
      <View
        style={[
          styles.inputRow,
          {
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.outlineVariant,
          },
        ]}
      >
        <TextInput
          value={message}
          onChangeText={setMessage}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          maxLength={maxLength}
          onKeyPress={handleKeyPress}
          onSubmitEditing={handleSend}
          multiline
          mode="flat"
          theme={{ roundness: 0 }}
          style={[styles.input, { backgroundColor: 'transparent' }]}
          textColor={theme.colors.onSurface}
          cursorColor={theme.colors.primary}
          underlineColor="transparent"
          activeUnderlineColor="transparent"
          placeholderTextColor={theme.colors.onSurfaceVariant}
          autoFocus={autoFocus}
        />

        <View style={styles.actionButtons}>
          <IconButton
            icon="paperclip"
            size={20}
            disabled={disabled}
            onPress={() => {
              // UI only for now; actual upload flow is handled elsewhere.
            }}
            style={styles.iconButton}
            iconColor={theme.colors.onSurfaceVariant}
            accessibilityLabel={t('chat.input.attach', 'Attach')}
          />

          {showVoiceInput ? (
            <IconButton
              icon="microphone"
              size={20}
              disabled={disabled}
              onPress={() => {
                // TODO: Implement voice input
              }}
              style={styles.iconButton}
              iconColor={theme.colors.onSurfaceVariant}
              accessibilityLabel={t('chat.input.mic', 'Voice input')}
            />
          ) : null}

          <IconButton
            icon="send"
            mode="contained"
            size={20}
            disabled={!canSend}
            onPress={handleSend}
            style={[
              styles.sendButton,
              {
                backgroundColor: canSend ? theme.colors.primary : theme.colors.surfaceDisabled,
              },
            ]}
            iconColor={canSend ? theme.colors.onPrimary : theme.colors.onSurfaceDisabled}
            accessibilityLabel={t('chat.input.send', 'Send message')}
          />
        </View>

      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 4,
    borderWidth: 1,
    borderRadius: 10,
    overflow: 'hidden',
    paddingLeft: 2,
    paddingRight: 5,
    paddingVertical: 3,
  },
  input: {
    flex: 1,
    minHeight: 44,
    maxHeight: 112,
    borderRadius: 0,
    fontSize: 15,
    lineHeight: 22,
    letterSpacing: 0,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingBottom: 2,
  },
  iconButton: {
    margin: 0,
    width: 38,
    height: 38,
    borderRadius: 10,
  },
  sendButton: {
    margin: 0,
    width: 40,
    height: 40,
    borderRadius: 10,
  },
});
